import { useId } from 'react'
import { getOverview } from '../../lib/api'
import type { SecureScoreReading } from '../../lib/api'
import { agrees, useSession } from './session'
import { useApi } from './useApi'
import './live.css'
import './secure-score.css'

/** Microsoft's own Secure Score page. Theirs to explain, so we send people there. */
const DEFENDER_URL = 'https://security.microsoft.com/securescore'

/**
 * Microsoft Secure Score, exactly as Microsoft reported it.
 *
 * THE ONLY PERCENTAGE IN THE PRODUCT, AND IT IS NOT OURS. Defenssive publishes no score of its own
 * (see CoverageLedger.tsx): a number out of a hundred from us would claim a whole-tenant verdict we
 * have not reached. This one is Microsoft's, read on each assessment. It is shown because customers
 * already know it and ask for it, and it is only safe to show because every rendering says, in
 * words beside it, whose number it is -- a gauge with our name above it and no attribution would
 * be the score this product refuses to publish, arrived at by the back door.
 *
 * Only a measured reading draws a gauge. "Microsoft has no score for this tenant", "we could not
 * read it" and "not read yet" are three different facts and none of them is a zero, so none gets
 * an empty arc: an empty gauge is a picture of 0 percent.
 *
 * The arc is the accent blue, never the severity ramp. Red here would be us judging Microsoft's
 * number, and the level is printed as text anyway, so colour carries nothing on its own.
 */
export function SecureScoreGauge({
  reading,
  category,
  compact = false,
}: {
  /** Null when no assessment has read it yet; undefined from a server that does not send it. */
  reading: SecureScoreReading | null | undefined

  /**
   * One of Microsoft's own categories (Identity, Data, Device, Apps…). Shows that category's
   * points instead of the whole score -- only ever Microsoft's breakdown, never one we derive.
   */
  category?: string

  /** As the last tile of an area page's insights strip: the same figure and sentences, no arc. */
  compact?: boolean
}) {
  const headingId = useId()
  const title = category ? `Microsoft Secure Score — ${category}` : 'Microsoft Secure Score'
  const shown = figureOf(reading, category)

  if (compact) {
    return (
      <div className="fi-tile fi-static fi-area-tile" role="group" aria-labelledby={headingId}>
        <span id={headingId} className="fi-label">{category ? `Secure Score, ${category.toLowerCase()}` : 'Secure Score'}</span>
        {shown.kind === 'figure' ? (
          <>
            <span className="fi-value">{shown.percent}<small>%</small></span>
            <span className="fi-sub">
              {shown.current} of {shown.max} points · Microsoft&apos;s own number, not a Defenssive score
            </span>
          </>
        ) : (
          <span className="fi-sub">{shown.sentence}</span>
        )}
        {reading?.status === 'measured' ? (
          <a className="fi-link" href={DEFENDER_URL} target="_blank" rel="noopener noreferrer">Open in Microsoft Defender</a>
        ) : null}
      </div>
    )
  }

  return (
    <section className="dash-panel secure-score" aria-labelledby={headingId}>
      {shown.kind === 'figure' ? (
        <Gauge
          percent={shown.percent}
          label={`Microsoft Secure Score${category ? ` for ${category}` : ''} ${shown.percent} percent`}
        />
      ) : null}

      <div className="secure-score-body">
        <h2 id={headingId} className="dash-panel-title">{title}</h2>
        <p className="secure-score-attribution">
          Microsoft&apos;s own rating of your tenant&apos;s security settings, as Microsoft reports
          it. Not a Defenssive score, and not the CISA controls.
        </p>

        {shown.kind === 'figure' ? (
          <>
            <p className="secure-score-points">
              {shown.current} of {shown.max} points
            </p>
            {/* Microsoft's date for the score, not the day we read it: the two can differ by a
                day, and the number belongs to the day Microsoft computed it. Omitted rather than
                replaced by our read date when Microsoft sent none. */}
            {shown.measuredAt ? (
              <p className="secure-score-note">Microsoft&apos;s score of {day(shown.measuredAt)}</p>
            ) : null}
          </>
        ) : (
          <p className="secure-score-state">{shown.sentence}</p>
        )}

        {/* Only where there is a score on Microsoft's side to go and look at. */}
        {reading?.status === 'measured' ? (
          <a className="secure-score-link" href={DEFENDER_URL} target="_blank" rel="noopener noreferrer">
            Open in Microsoft Defender
          </a>
        ) : null}
      </div>
    </section>
  )
}

/**
 * An area page's part of Microsoft Secure Score, from the overview.
 *
 * Renders NOTHING until it has an answer, and nothing if the read fails or belongs to another
 * workspace. The findings list below it is the page; a side panel that failed must not put an
 * error above the list, and the list carries its own freshness and its own failure wording.
 */
export function AreaSecureScore({ category, compact = false }: { category: string; compact?: boolean }) {
  const session = useSession()
  const overview = useApi(() => getOverview(), [])

  if (overview.state !== 'ready') return null

  // Same check the dashboard makes: a number captioned with one workspace's name must be that
  // workspace's number.
  if (!agrees(session, overview.data.workspaceId)) return null

  return <SecureScoreGauge reading={overview.data.secureScore} category={category} compact={compact} />
}

/* ── the arc ───────────────────────────────────────────────────────────────────────────── */

function Gauge({ percent, label }: { percent: number; label: string }) {
  // pathLength 100 makes the dash length the percentage itself -- no trigonometry to get wrong.
  // Clamped for drawing only: the printed number is Microsoft's, whatever it is.
  const drawn = Math.min(100, Math.max(0, percent))

  return (
    <div className="secure-score-gauge" role="img" aria-label={label}>
      <svg viewBox="0 0 120 66" aria-hidden="true" focusable="false">
        <path className="secure-score-track" d={ARC} pathLength={100} fill="none" strokeWidth={10} />
        {drawn > 0 ? (
          <path
            className="secure-score-arc"
            d={ARC}
            pathLength={100}
            fill="none"
            strokeWidth={10}
            strokeDasharray={`${drawn} 100`}
          />
        ) : null}
      </svg>
      <span className="secure-score-value" aria-hidden="true">
        {percent}
        <span className="secure-score-unit">%</span>
      </span>
    </div>
  )
}

/** A half circle, left to right over the top. */
const ARC = 'M 10 60 A 50 50 0 0 1 110 60'

/* ── what to show ──────────────────────────────────────────────────────────────────────── */

type Shown =
  | { kind: 'figure'; percent: number; current: number; max: number; measuredAt: string | null }
  | { kind: 'words'; sentence: string }

/**
 * The number to draw, or the sentence that stands where a number would be.
 *
 * Every non-number branch is a sentence about what we know, never a zero. A category Microsoft did
 * not break out is said to be missing; it is not estimated from the total, because an estimate
 * printed in a gauge labelled "Microsoft" would be our number wearing their name.
 */
function figureOf(reading: SecureScoreReading | null | undefined, category: string | undefined): Shown {
  if (!reading) {
    return { kind: 'words', sentence: "Not read yet — it's read on the next assessment." }
  }

  if (reading.status === 'absent') {
    return {
      kind: 'words',
      sentence:
        "Microsoft doesn't report a Secure Score for this tenant (it may not be licensed for it).",
    }
  }

  // Anything that is not a measurement lands here, including a status this build has never heard
  // of: it is shown as "could not read", never as a reading.
  if (reading.status !== 'measured') {
    return {
      kind: 'words',
      sentence:
        `We couldn't read Microsoft Secure Score on the last assessment (${day(reading.readAt)}). ` +
        'Nothing is concluded from that.',
    }
  }

  if (category) {
    const entry = (reading.categories ?? []).find(
      (c) => c.category.toLowerCase() === category.toLowerCase())

    if (!entry) {
      return {
        kind: 'words',
        sentence: "Microsoft's breakdown by category wasn't available on the last assessment.",
      }
    }

    return figure(entry.score, entry.maxScore, reading.measuredAt)
  }

  return figure(reading.currentScore, reading.maxScore, reading.measuredAt)
}

function figure(current: number | null, max: number | null, measuredAt: string | null): Shown {
  // A reading with no usable denominator has no percentage. Dividing anyway prints NaN or
  // Infinity; defaulting prints a number Microsoft never gave.
  if (current === null || max === null || !Number.isFinite(current) || !Number.isFinite(max) || max <= 0) {
    return {
      kind: 'words',
      sentence: "Microsoft's reading on the last assessment came without usable numbers, so none is shown.",
    }
  }

  return {
    kind: 'figure',
    percent: Math.round((current / max) * 100),
    current: Math.round(current),
    max: Math.round(max),
    measuredAt,
  }
}

/** A date without a time, as the rest of the dashboard writes one. */
function day(value: string): string {
  const when = new Date(value)
  return Number.isNaN(when.getTime()) ? value : when.toLocaleDateString(undefined, { dateStyle: 'long' })
}
